// apps/web/src/pages/author/constraint-config/dispatcher.tsx
//
// Phase 9 Plan 10 Wave-4 SIBLING of apps/web/src/pages/author/widget-config/
// dispatcher.tsx — the per-CONSTRAINT-KIND config sub-form dispatcher (CSTR-01).
// Where the Phase 8 widget-config dispatcher routes a configSchema editor by
// templateId, this dispatcher routes a constraint authoring sub-form by
// constraint kind ("cardinality" / "propagation" / "visibility" / "dependency"
// / "selectability").
//
// 4-layer WIDG-10 defense mirrored from widget-config/dispatcher.tsx:
//   Layer 1 (compile-time):   `ReadonlyMap<ConstraintKind, ConstraintConfigSubFormFC>`
//     erases .set / .delete — see the @ts-expect-error in dispatcher.test.tsx
//     that proves the type-erasure remains in place.
//   Layer 2 (runtime throw):  not applicable (no plugin loader on the author tier).
//   Layer 3 (runtime freeze): `Object.freeze(_constraintSubForms)` — THIS FILE.
//   Layer 4 (static analysis): Plan 09-13 extends scripts/check-registry-closed.sh
//     to gate `.set(` / `.delete(` callsites on this Map outside the canonical
//     5-kind allowlist. Phase 9 ships the mirrored shape — Plan 09-13 wires the
//     grep gate.
//
// All 5 constraint kinds map to sub-forms shipped by Plan 09-07 (cardinality /
// propagation / visibility / dependency) and Plan 09-08 (selectability). The
// fallback Alert is the documented unknown-kind UX (defense-in-depth for hand-
// crafted JSON paste payloads / older recipes opened in a newer client).
//
// Critical correctness note (RESEARCH §"Pitfall 2"): Object.freeze on a Map
// does NOT block .set() / .delete() because Map internals live in slots, not
// own properties. The ReadonlyMap type erasure (Layer 1) blocks at compile
// time; Object.freeze (Layer 3) freezes the OBJECT shell. Belt-and-suspenders.

import type { Constraint } from "@the-recipe-spread/shared";
import { AlertCircle } from "lucide-react";
import type * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CardinalityConfigSubForm } from "./cardinality-config.js";
import { DependencyConfigSubForm } from "./dependency-config.js";
import { PropagationConfigSubForm } from "./propagation-config.js";
import { SelectabilityConfigSubForm } from "./selectability-config.js";
import type { ConstraintConfigSubFormFC, ConstraintConfigSubFormProps } from "./types.js";
import { VisibilityConfigSubForm } from "./visibility-config.js";

// ConstraintKind — closed literal union derived from the Constraint discriminated
// union. Phase 01.1 ships 5 kinds. Adding a 6th constraint kind without updating
// this dispatcher fails the typecheck below (the Map entry list is checked
// against the union).
export type ConstraintKind = Constraint["kind"];

// All 5 per-kind constraint config sub-forms. Plan 09-07 owns cardinality /
// propagation / visibility / dependency; Plan 09-08 owns selectability.
const _constraintSubForms = new Map<ConstraintKind, ConstraintConfigSubFormFC>([
  ["cardinality", CardinalityConfigSubForm as ConstraintConfigSubFormFC],
  ["propagation", PropagationConfigSubForm as ConstraintConfigSubFormFC],
  ["visibility", VisibilityConfigSubForm as ConstraintConfigSubFormFC],
  ["dependency", DependencyConfigSubForm as ConstraintConfigSubFormFC],
  ["selectability", SelectabilityConfigSubForm as ConstraintConfigSubFormFC],
]);

// Layer 3: freeze the Map shell.
export const constraintSubForms: ReadonlyMap<ConstraintKind, ConstraintConfigSubFormFC> =
  Object.freeze(_constraintSubForms);

export interface ConstraintFormDispatcherProps {
  /** Discriminator — drives the dispatch. May be a non-kind string (e.g. from a
   * hand-crafted JSON paste payload); in that case the fallback Alert renders. */
  constraintKind: string;
  /** Stable id of the constraint instance (position-derived per Q-02). */
  constraintId: string;
  /** RHF form path to the root of THIS constraint, e.g. "constraints.0". */
  basePath: string;
}

// ConstraintFormDispatcher — routes by constraintKind to the matching
// ConstraintConfigSubForm. Unknown kind (including any hand-crafted paste
// payload that carries a kind outside the closed registry) renders the
// documented destructive Alert per T-09-10-02.
export function ConstraintFormDispatcher({
  constraintKind,
  constraintId,
  basePath,
}: ConstraintFormDispatcherProps): React.ReactElement {
  const SubForm = constraintSubForms.get(constraintKind as ConstraintKind);
  if (SubForm === undefined) {
    return (
      <Alert variant="destructive">
        <AlertCircle size={16} />
        <AlertTitle>Constraint editor unavailable</AlertTitle>
        <AlertDescription>
          Configuration UI not available for this constraint kind.
        </AlertDescription>
      </Alert>
    );
  }
  const subFormProps: ConstraintConfigSubFormProps = { constraintId, basePath };
  return <SubForm {...subFormProps} />;
}
ConstraintFormDispatcher.displayName = "ConstraintFormDispatcher";
